<!--
   generic reusable component to handle nav items as lists
   <NavMenuItemList
        items="..."     # Mandatory - array of items with the nav item formatting
        Emits:
        - close         # fired when component needs to close the side panel
        - itemClick     # fired on click on an item that does not trigger a direct routing (click on an item that triggers a submenu opening)
   />
 -->
<script setup lang="ts">
/* COMPOSABLES */
import { useNavMenu } from '@/composables/useNavMenu';
import { checkExternalRedirect } from '@/helpers/navigation';
/* TYPES */
import type { NavItemType } from '@/types/navigation/navigation.types';
import { ALChip, useCssBreakpoints } from '@af-lib/vue-ui';

type Props = {
  /* array of items to display as list */
  items: NavItemType[];
};
const props = defineProps<Props>();
const emits = defineEmits(['itemClick', 'close']);

const { tabletAndUp } = useCssBreakpoints();


const { hasSubmenu, itemRoute, isActive } = useNavMenu();

// FUNCTIONS
/**
 * Called on item click - either triggers redirection or an emit
 * @param {NavItemType} item item clicked
 */
function clickMenu(item: NavItemType) {
  if (hasSubmenu(item)) {
    emits('itemClick', item);
  } else {
    checkExternalRedirect(item);
    emits('close');
  }
}

function isItemNew(item: NavItemType): boolean {
  return typeof item.isNew === 'function' ? item.isNew() : !!item.isNew;
}
</script>

<template>
  <v-list class="text-fontSecondary">
    <v-tooltip
      v-for="(item, i) in props.items"
      :key="i"
      :disabled="item.isDisabled ? !item.isDisabled().disabled : true"
    >
      <template #activator="{ props: tooltipProps }">
        <div v-bind="tooltipProps">
          <v-list-item
            :class="[{ 'text-primary font-weight-medium': isActive(item) }, item.class, 'py-4 px-4']"
            :active="false"
            :ripple="false"
            :to="itemRoute(item)"
            role="button"
            :aria-label="$t(item.label)"
            :disabled="item.isDisabled ? item.isDisabled().disabled : false"
            @click="clickMenu(item)"
          >
            <div class="d-flex w-100">
              <v-icon v-if="item.icon" :icon="item.icon" class="ms-1 me-3 text-icon-3" />
              <div class="d-flex justify-space-between align-center w-100">
                <v-list-item-title
                  :class="[
                    { 'font-weight-medium': isActive(item) },
                    tabletAndUp ? 'text-body-3' : 'text-body-2',
                    'font-weight-regular'
                  ]"
                >
                  {{ $t(item.label) }}
                </v-list-item-title>
                <v-icon
                  v-if="hasSubmenu(item)"
                  role="button"
                  class="text-body-2 text-fontPrimary"
                  icon="fa-chevron-right"
                  :aria-label="$t('accessibility.menu.go_to', { section_name: $t(item.label) })"
                />
                <ALChip
                  v-if="isItemNew(item)"
                  :label="$t('general.new')"
                  :isInteractive="false"
                  :isClosable="false"
                  class="border-transparent ms-3 gradient-border-PT text-sm"
                  size="s"
                />
              </div>
            </div>
          </v-list-item>
        </div>
      </template>
      {{ item.isDisabled ? item.isDisabled().tooltip : '' }}
    </v-tooltip>
  </v-list>
</template>
<style scoped lang="scss">
:deep(.v-list-item__prepend > .v-icon) {
  margin-inline-end: 12px;
}
</style>
